import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, Enums, inject, subUnitsOfTime, toArray } from 'Mri.Web.Common';
import * as React from 'react';
import { BsBoxArrowUpRight } from '@react-icons/all-files/bs/BsBoxArrowUpRight';
import { BsFolder } from '@react-icons/all-files/bs/BsFolder';

import { FileSystemItem } from './FileSystemItem';
import { FileSystemsStore } from './FileSystemsStore';

export interface IProductTypeListProps {
    readonly fileSystems: Mri.FileSystems.FileSystem[];
    readonly fileSystemType: Mri.DisplayApi.FileSystemTypes;
    readonly productType: number;
    readonly forceOpen: boolean;
}

@autoinject
@observer
export class ProductTypeList extends React.Component<IProductTypeListProps> {
    public async componentDidMount() {
        try {
            await this.fileSystemsStore.loadMetaAsync();
        } catch (e: any) {
            console.error(e);
        }
    }

    public render() {
        const collapsed = this.collapsed && !this.props.forceOpen;
        return (
            <>
                <tr className="foldering-system-row">
                    <td colSpan={4}>
                        <a onClick={this.toggleCollapse} className="mx-4">
                            <em> {collapsed ? <BsFolder /> : <BsFolder />} </em>

                            <b className="px-2">{this.productType}</b>

                            {this.hasNewItem && <span className="badge bg-primary">New</span>}
                        </a>
                    </td>
                    <td colSpan={2} className="align-middle">
                        {this.meta && (
                            <div className="py-1 btn-group btn-group-sm mx-2 float-end">
                                <div className="btn btn-link no-hover">
                                    <BsBoxArrowUpRight />
                                </div>
                                {this.meta.internalChangelog && (
                                    <a href={this.meta.internalChangelog} className="btn btn-link" target="_blank">
                                        Internal Changelog
                                    </a>
                                )}
                                {this.meta.publicChangelog && (
                                    <a href={this.meta.publicChangelog} className="btn btn-link" target="_blank">
                                        Public Changelog
                                    </a>
                                )}
                            </div>
                        )}
                    </td>
                </tr>
                {!collapsed && this.fileSystems?.map(f => <FileSystemItem fileSystem={f} key={f.fileSystemGuid} />)}
            </>
        );
    }

    @computed
    private get fileSystemsByProdType() {
        const aggregate: Map<number, Mri.FileSystems.FileSystem[]> = new Map<
        Mri.DisplayApi.FileSystemTypes,
        Mri.FileSystems.FileSystem[]
        >();

        this.props.fileSystems.reduce((agg, value) => {
            const key = value.softwareVersion.productType;
            const arr = agg.get(key) ?? [];
            arr.push(value);
            return agg.set(key, arr);
        }, aggregate);

        return aggregate;
    }

    @computed
    private get fileSystems() {
        return this.fileSystemsByProdType.get(this.props.productType);
    }

    @computed
    private get productType() {
        const { fileSystemType, productType } = this.props;

        const productTypeNames = Enums.FileSystemProductTypes.get(fileSystemType);
        if (productTypeNames) {
            const name = productTypeNames.get(productType);
            if (name) {
                return `${productType} - ${name.replace(/_/g, ' ')}`;
            } else {
                return productType;
            }
        } else {
            return productType;
        }
    }

    @computed
    private get hasNewItem() {
        if (!this.fileSystemsByProdType) return false;

        return !!toArray(this.fileSystemsByProdType)
            .flat()
            .find(fs => (fs.updated ? fs.updated > subUnitsOfTime(new Date(), 48, 'hours') : false));
    }

    @computed
    private get meta() {
        return this.fileSystems?.length
            ? this.fileSystemsStore.fileSystemMeta.get(
                `${this.fileSystems[0].softwareVersion.fsType}.${this.fileSystems[0].softwareVersion.productType}`
            )
            : undefined;
    }

    @bind
    @action
    private toggleCollapse() {
        this.collapsed = !this.collapsed;
    }

    @observable private collapsed = true;
    @inject
    private fileSystemsStore!: FileSystemsStore;
}
